// Spec 28 PR 1 — SummaryCard.
//
// The final widget. Pure presentation. Signature element: the
// done-checks stagger in one by one with a small bullet rule
// drawing in, then the next-action pills appear together with one
// of them claiming primary visual weight.

import { ArrowRight } from "lucide-react";
import { Link } from "react-router";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface SummaryCardProps {
	title: string;
	body?: string | null;
	doneItems: string[];
	nextActions: {
		id: string;
		label: string;
		href: string;
		kind: "primary" | "secondary";
	}[];
}

export function SummaryCard({ props }: WidgetRenderProps<SummaryCardProps>) {
	return (
		<div className="py-12 space-y-7">
			<div className="text-center space-y-2">
				<div className="text-[28px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
					{props.title}
				</div>
				{props.body && (
					<div
						className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto leading-relaxed anim-fade-in"
						style={{ animationDelay: "120ms" }}
					>
						{props.body}
					</div>
				)}
			</div>

			<div className="max-w-[400px] mx-auto space-y-1.5">
				{props.doneItems.map((item, i) => (
					<div
						key={item}
						className="flex items-center gap-3 anim-fade-rise"
						style={{ animationDelay: `${220 + i * 90}ms` }}
					>
						<span className="text-[var(--color-success)] text-[12px]">✓</span>
						<span className="text-[13px] text-[var(--color-text)]">
							{item}
						</span>
					</div>
				))}
			</div>

			<div
				className="flex flex-wrap justify-center gap-2 anim-fade-rise"
				style={{ animationDelay: `${220 + props.doneItems.length * 90 + 120}ms` }}
			>
				{props.nextActions.map((action) => {
					const isPrimary = action.kind === "primary";
					return (
						<Link key={action.id} to={action.href}>
							<Pill
								className={
									isPrimary
										? "!h-11 !px-5 !text-[14px]"
										: "!h-9 !px-3.5 !text-[12px] opacity-90 hover:opacity-100"
								}
							>
								{action.label}
								{isPrimary && <ArrowRight className="w-4 h-4" />}
							</Pill>
						</Link>
					);
				})}
			</div>
		</div>
	);
}
